import { useQuery } from '@tanstack/react-query';
import { notificationsApi, type NotificationEvent } from '../api/client';
import type { Notification } from '@aaimplatform/ui';

/**
 * Adapt the spec-typed `NotificationEvent` (from platform-admin
 * `/notifications/recent`) into the `@aaimplatform/ui` `Notification`
 * shape consumed by `<NotificationPanel>`. The two shapes diverged
 * historically: the API surface uses `body`/`createdAt`/`read` while the
 * UI primitive uses `message`/`timestamp`/`unread`. Mapping here keeps the
 * transport contract honest (typed end-to-end) while the UI primitive
 * remains domain-neutral.
 */
function toUiNotification(event: NotificationEvent): Notification {
  return {
    id: event.id,
    title: event.title,
    message: event.body,
    // The instant, formatted by the panel in the viewer's zone (GH #2296).
    timestamp: event.createdAt,
    unread: !event.read,
  };
}

export function useNotifications() {
  return useQuery<Notification[], Error>({
    queryKey: ['notifications'],
    queryFn: async () => {
      const events = await notificationsApi.getRecent();
      return events.map(toUiNotification);
    },
    refetchInterval: 120000,
    refetchIntervalInBackground: false,
  });
}
